import type { DayOperatingHours } from '../types';

export const WEEKDAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type Weekday = (typeof WEEKDAY_ORDER)[number];
export type OperatingHoursMap = Record<Weekday, DayOperatingHours>;

export interface ScheduleEntry {
  days: string[];
  allDay?: boolean;
  start: string | null;
  end: string | null;
}

export const DEFAULT_TENANT_OPERATING_HOURS: OperatingHoursMap = {
  mon: { enabled: true, allDay: false, start: '09:00', end: '20:00' },
  tue: { enabled: true, allDay: false, start: '09:00', end: '20:00' },
  wed: { enabled: true, allDay: false, start: '09:00', end: '20:00' },
  thu: { enabled: true, allDay: false, start: '09:00', end: '20:00' },
  fri: { enabled: true, allDay: false, start: '09:00', end: '20:00' },
  sat: { enabled: true, allDay: false, start: '09:00', end: '14:00' },
  sun: { enabled: false, allDay: false, start: null, end: null },
};

/**
 * Resolves the tenant context id used by tenant-scoped frontend requests.
 * The current switched tenant has priority over the user's default tenant.
 */
export function resolveTenantContextId(
  currentTenantId?: string | null,
  userTenantId?: string | null
): string | null {
  return currentTenantId || userTenantId || null;
}

/**
 * Normalizes tenant operating hours into a full week structure.
 * Missing days or malformed payloads fallback to project defaults.
 */
export function normalizeTenantOperatingHours(
  source?: Partial<Record<string, DayOperatingHours>> | null
): OperatingHoursMap {
  const normalized: OperatingHoursMap = { ...DEFAULT_TENANT_OPERATING_HOURS };
  if (!source || typeof source !== 'object') {
    return normalized;
  }

  WEEKDAY_ORDER.forEach((day) => {
    const dayConfig = source[day];
    if (!dayConfig || typeof dayConfig !== 'object') {
      return;
    }
    const enabled = Boolean(dayConfig.enabled);
    const allDay = enabled && Boolean(dayConfig.allDay ?? (dayConfig as { all_day?: boolean }).all_day);
    normalized[day] = {
      enabled,
      allDay,
      start: enabled && !allDay ? dayConfig.start ?? null : null,
      end: enabled && !allDay ? dayConfig.end ?? null : null,
    };
  });

  return normalized;
}

/**
 * Builds grouped schedule entries based on enabled days and identical time windows.
 * Days are always sorted in canonical week order.
 */
export function buildScheduleEntriesFromOperatingHours(
  operatingHours: Partial<Record<string, DayOperatingHours>> | null | undefined
): ScheduleEntry[] {
  const normalized = normalizeTenantOperatingHours(operatingHours ?? undefined);

  const groups: Record<string, string[]> = {};
  WEEKDAY_ORDER.forEach((day) => {
    const config = normalized[day];
    if (!config.enabled) {
      return;
    }
    if (config.allDay) {
      const key = 'all_day';
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(day);
      return;
    }
    if (!config.start || !config.end) {
      return;
    }
    const key = `${config.start}|${config.end}`;
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(day);
  });

  return Object.entries(groups).map(([timeRange, days]) => {
    const sortedDays = [...days].sort(
      (a, b) => WEEKDAY_ORDER.indexOf(a as Weekday) - WEEKDAY_ORDER.indexOf(b as Weekday)
    );
    if (timeRange === 'all_day') {
      return { days: sortedDays, allDay: true, start: null, end: null };
    }
    const [start, end] = timeRange.split('|');
    return { days: sortedDays, allDay: false, start, end };
  });
}

/**
 * Extracts the tenant IVR audio cap from API payload, supporting camelCase and snake_case.
 */
export function extractTenantIvrMaxAudioSeconds(
  payload: Record<string, unknown> | null | undefined,
  fallback = 27
): number {
  const rawValue = payload?.ivrMaxAudioSeconds ?? payload?.ivr_max_audio_seconds;
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return fallback;
  }
  return Math.floor(parsed);
}
